import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Plus, Trash2, ArrowUp, ArrowDown, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/truck-boxes`;

export default function TruckBoxStandard({ onClose }) {
  const [items, setItems] = useState(null);
  const [meta, setMeta] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/standard`).then(r => { setItems(r.data.items.map(i => ({ ...i }))); setMeta(r.data); }).catch(() => toast.error('Could not load the standard list'));
  }, []);

  const upd = (idx, patch) => setItems(prev => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const move = (idx, dir) => setItems(prev => {
    const n = [...prev]; const j = idx + dir;
    if (j < 0 || j >= n.length) return prev;
    [n[idx], n[j]] = [n[j], n[idx]];
    return n;
  });
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/standard`, { items: items.filter(i => i.name.trim()) });
      toast.success(`Standard list saved — ${r.data.items.length} items`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-3 sm:p-6 overflow-y-auto" data-testid="truck-std-modal">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div>
            <h3 className="font-bold text-slate-900">Standard truck box list</h3>
            <p className="text-xs text-slate-500">What every service truck should carry. Changes apply to new boxes; use "Add new standard items" on a truck to pull additions in.{meta.updated_at ? ` Last saved ${new Date(meta.updated_at).toLocaleDateString()}${meta.updated_by ? ` by ${meta.updated_by}` : ''}.` : ''}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-900/5 text-slate-700" data-testid="truck-std-close"><X className="w-5 h-5" /></button>
        </div>
        {!items ? <p className="p-6 text-slate-500 text-sm">Loading…</p> : (
          <div className="p-3 sm:p-4">
            <div className="max-h-[60vh] overflow-y-auto divide-y divide-white/5">
              {items.map((it, idx) => (
                <div key={it.id || idx} className="flex items-center gap-2 py-1.5" data-testid={`truck-std-row-${idx}`}>
                  <span className="w-6 text-right text-[11px] text-slate-500 font-mono">{idx + 1}</span>
                  <input value={it.name} onChange={e => upd(idx, { name: e.target.value })} data-testid={`truck-std-name-${idx}`}
                    className="flex-1 px-2.5 py-1.5 rounded-lg bg-white text-slate-900 text-sm outline-none focus:ring-2 focus:ring-sky-400" />
                  <input type="number" inputMode="decimal" min="0" value={it.qty} onChange={e => upd(idx, { qty: e.target.value })} data-testid={`truck-std-qty-${idx}`}
                    className="w-16 px-2 py-1.5 rounded-lg bg-white text-slate-900 text-sm text-center font-bold" />
                  <button onClick={() => move(idx, -1)} className="p-1.5 rounded hover:bg-slate-900/5 text-slate-500" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                  <button onClick={() => move(idx, 1)} className="p-1.5 rounded hover:bg-slate-900/5 text-slate-500" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setItems(prev => prev.filter((_, i) => i !== idx))} data-testid={`truck-std-del-${idx}`} className="p-1.5 rounded hover:bg-red-50 text-red-700" title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
              <button onClick={() => setItems(prev => [...prev, { id: null, name: '', qty: 1 }])} data-testid="truck-std-add"
                className="px-3 py-2 rounded-xl bg-slate-900/5 hover:bg-slate-900/10 text-slate-900 text-sm font-semibold flex items-center gap-1.5"><Plus className="w-4 h-4" /> Add item</button>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">{items.length} items</span>
                <button onClick={save} disabled={saving} data-testid="truck-std-save" className="px-4 py-2 rounded-xl bg-emerald-600 disabled:bg-slate-300 text-white font-bold text-sm flex items-center gap-1.5"><Save className="w-4 h-4" /> Save list</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
